/**
 * DemographicTargeting Component
 * Controls for age, gender, income, and parental status targeting
 *
 * Features:
 * - Age range multi-select (18-24, 25-34, etc.)
 * - Gender selection (Male, Female, Unknown)
 * - Household income tiers
 * - Parental status
 */

import React, { useCallback } from 'react';
import { Checkbox } from '@/components/ui/Checkbox';

// Demographic options based on Google Ads API enums
const AGE_RANGES = [
  { value: 'AGE_RANGE_18_24', label: '18-24' },
  { value: 'AGE_RANGE_25_34', label: '25-34' },
  { value: 'AGE_RANGE_35_44', label: '35-44' },
  { value: 'AGE_RANGE_45_54', label: '45-54' },
  { value: 'AGE_RANGE_55_64', label: '55-64' },
  { value: 'AGE_RANGE_65_UP', label: '65+' },
  { value: 'AGE_RANGE_UNDETERMINED', label: 'Unknown' },
];

const GENDERS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'UNDETERMINED', label: 'Unknown' },
];

const INCOME_RANGES = [
  { value: 'INCOME_RANGE_0_50', label: 'Lower 50%' },
  { value: 'INCOME_RANGE_50_60', label: '50-60%' },
  { value: 'INCOME_RANGE_60_70', label: '60-70%' },
  { value: 'INCOME_RANGE_70_80', label: '70-80%' },
  { value: 'INCOME_RANGE_80_90', label: '80-90%' },
  { value: 'INCOME_RANGE_90_UP', label: 'Top 10%' },
  { value: 'INCOME_RANGE_UNDETERMINED', label: 'Unknown' },
];

const PARENTAL_STATUSES = [
  { value: 'PARENT', label: 'Parent' },
  { value: 'NOT_A_PARENT', label: 'Not a Parent' },
  { value: 'UNDETERMINED', label: 'Unknown' },
];

export interface DemographicSettings {
  ageRanges: string[];
  genders: string[];
  incomeRanges: string[];
  parentalStatuses: string[];
}

interface DemographicTargetingProps {
  value: DemographicSettings;
  onChange: (settings: DemographicSettings) => void;
  disabled?: boolean;
  compact?: boolean;
}

export const DEFAULT_DEMOGRAPHIC_SETTINGS: DemographicSettings = {
  ageRanges: [], // Empty = all ages
  genders: [], // Empty = all genders
  incomeRanges: [], // Empty = all incomes
  parentalStatuses: [], // Empty = all parental statuses
};

export function DemographicTargeting({
  value,
  onChange,
  disabled = false,
  compact = false,
}: DemographicTargetingProps) {
  // Helper to toggle a value in a list
  const toggleValue = useCallback((category: keyof DemographicSettings, val: string) => {
    const current = value[category];
    const updated = current.includes(val)
      ? current.filter(v => v !== val)
      : [...current, val];
    onChange({ ...value, [category]: updated });
  }, [value, onChange]);

  // Helper to select all values in a category
  const selectAll = useCallback((category: keyof DemographicSettings, options: Array<{ value: string }>) => {
    onChange({ ...value, [category]: options.map(o => o.value) });
  }, [value, onChange]);

  // Helper to clear all values in a category
  const clearAll = useCallback((category: keyof DemographicSettings) => {
    onChange({ ...value, [category]: [] });
  }, [value, onChange]);

  // Check if a category has all values selected
  const isAllSelected = useCallback((category: keyof DemographicSettings, options: Array<{ value: string }>) => {
    return options.every(o => value[category].includes(o.value));
  }, [value]);

  const renderCategory = (
    title: string,
    category: keyof DemographicSettings,
    options: Array<{ value: string; label: string }>,
    helperText: string
  ) => {
    const selectedCount = value[category].length;
    const allSelected = isAllSelected(category, options);

    return (
      <div className={compact ? 'space-y-2' : 'space-y-3'}>
        <div className="flex items-center justify-between">
          <h4 className={`font-medium text-gray-900 ${compact ? 'text-sm' : ''}`}>
            {title}
            {selectedCount > 0 && (
              <span className="ml-2 text-gray-500 font-normal">
                ({selectedCount === options.length ? 'All' : selectedCount})
              </span>
            )}
          </h4>
          {!disabled && (
            <div className="flex gap-2 text-xs">
              {!allSelected && (
                <button
                  type="button"
                  onClick={() => selectAll(category, options)}
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Select All
                </button>
              )}
              {selectedCount > 0 && (
                <button
                  type="button"
                  onClick={() => clearAll(category)}
                  className="text-gray-500 hover:text-gray-600 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
        <div className={`grid ${compact ? 'grid-cols-3 gap-2' : 'grid-cols-2 md:grid-cols-4 gap-2'}`}>
          {options.map(option => (
            <label
              key={option.value}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${value[category].includes(option.value)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <Checkbox
                checked={value[category].includes(option.value)}
                onChange={() => toggleValue(category, option.value)}
                disabled={disabled}
              />
              <span className={`text-gray-900 ${compact ? 'text-xs' : 'text-sm'}`}>
                {option.label}
              </span>
            </label>
          ))}
        </div>
        {selectedCount === 0 && !disabled && (
          <p className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}>
            No selection = target {helperText}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {renderCategory('Age Ranges', 'ageRanges', AGE_RANGES, 'all age ranges')}
      {renderCategory('Gender', 'genders', GENDERS, 'all genders')}
      {renderCategory('Household Income', 'incomeRanges', INCOME_RANGES, 'all income levels')}
      {renderCategory('Parental Status', 'parentalStatuses', PARENTAL_STATUSES, 'all parental statuses')}
    </div>
  );
}

// Compact version for use in creation wizards
export function DemographicTargetingCompact(props: Omit<DemographicTargetingProps, 'compact'>) {
  return <DemographicTargeting {...props} compact />;
}
